/* global document */
/* global window */
/* global Raven */

import React from 'react';
import ReactDOM from 'react-dom';
import { StripeProvider, Elements } from 'react-stripe-elements';

import 'normalize.css/normalize.css';

import {
  createStore,
  combineReducers,
  applyMiddleware,
} from 'redux';

import { Provider } from 'react-redux';
import { Router, Route, browserHistory, IndexRoute } from 'react-router';
import {
  syncHistoryWithStore,
  routerReducer, routerMiddleware,
} from 'react-router-redux';

import moment from 'moment';
import 'moment/locale/it';

import appReducer from '@arreeba/ristoranti-frontend-lib/lib/reducers/app';
import bookingReducer from '@arreeba/ristoranti-frontend-lib/lib/reducers/booking';
import orderReducer from '@arreeba/ristoranti-frontend-lib/lib/reducers/order';
import userReducer from '@arreeba/ristoranti-lib/lib/reducer/user';
import voucherReducer from '@arreeba/ristoranti-lib/lib/reducer/voucher';

import userMiddleware from '@arreeba/ristoranti-lib/lib/middleware/user';

import './budicon.css';
import './style.scss';

import Env from './lib/env';

import uxReducer from './reducers/ux';
import sideEffectsMiddleware from './middleware/side-effects';
import userWidgetMiddleware from './middleware/user-widget';
import { serviceSelector, mapDispatchToProps } from './util';

import load from './components/load';
import Root from './containers/root/';
import App from './containers/app/';
import {
  BookingContainer,
  BookingStart,
  BookingSelectDate,
  BookingTimeSeats,
  BookingForm,
  BookingEnd,
} from './containers/booking/';

import {
  OrderContainer,
  OrderStart,
  OrderStartWrapper,
  OrderSelectDateTime,
  OrderDeliveryAddress,
  Menu,
  OrderCart,
  OrderCheckout,
  OrderVoucher,
  OrderPayment,
  OrderEnd,
  OrderNoDelivery,
} from './containers/order';

import {
  SigninSignupPage,
} from './containers/user';


moment.locale('IT_it');

const environment = Env.getCurrent();

document.body.setAttribute('data-app-version', environment.version);

if (process.env.NODE_ENV !== 'development') {
  Raven
    .config(
      'https://1a3b6def2f6c4a2cb0d4bd402d5901e1@sentry.io/137729', {
        environment: environment.name,
        release: environment.version,
      })
    .install();
}

const reducers = combineReducers({
  app: appReducer(environment),
  user: userReducer,
  booking: bookingReducer,
  routing: routerReducer,
  ux: uxReducer,
  order: orderReducer,
  voucher: voucherReducer,
});


const store = createStore(reducers, applyMiddleware(
  routerMiddleware(browserHistory),
  sideEffectsMiddleware,
  userMiddleware(userWidgetMiddleware),
));

const history = syncHistoryWithStore(browserHistory, store);

const loadConfiguration = (nextState, replace, callback) => {
  const actions = mapDispatchToProps(store.dispatch);
  const bundle = nextState.params.appId;

  if (bundle) {
    actions.app.switchBundleId(bundle);
  }

  const state = store.getState();
  const services = serviceSelector(state);
  services.app.loadConfiguration(actions.app)
    .then(callback);
};

const widgetLoad = () => {
  const data = { eventName: 'it.ristoranti.widget.onload' };
  parent.postMessage(JSON.stringify(data), '*');
};

window.addEventListener('message', widgetLoad, false);

const isInIframe = window.self !== window.top;

window.addEventListener('load', () => {
  const stripe = Stripe(environment.stripe_key, { apiVersion: '2019-09-09' });

  ReactDOM.render(
    <StripeProvider stripe={stripe}>
      <Elements locale="it">
        <Provider store={store}>
          <Router history={history}>
            <Route path="/" component={Root} onEnter={loadConfiguration}>
              <Route
                path="/app/:appId"
                component={load(store, App, null, 'app')}
              />

              <Route path="/app/:appId/order" component={OrderContainer}>
                <IndexRoute component={OrderStart} />

                <Route path="no-delivery" component={OrderNoDelivery} />

                <Route path=":orderType" component={OrderStartWrapper} />
                <Route path=":orderType/address" component={OrderDeliveryAddress} />
                <Route path=":orderType/:restaurantId" component={OrderSelectDateTime} />
                <Route path=":orderType/:restaurantId/:orderDate" component={OrderSelectDateTime} />
                <Route
                  path=":orderType/:restaurantId/:orderDate/menu"
                  component={Menu}
                />
                <Route path=":orderType/:restaurantId/:orderDate/cart" component={OrderCart} />
                <Route path=":orderType/:restaurantId/:orderDate/voucher" component={OrderVoucher} />
                <Route path=":orderType/:restaurantId/:orderDate/checkout" component={OrderCheckout} />
                <Route path=":orderType/:restaurantId/:orderDate/payment" component={OrderPayment} />
                <Route path=":orderType/:restaurantId/:orderDate/end" component={OrderEnd} />
              </Route>

              <Route
                path="/app/:appId/booking"
                component={load(store, BookingContainer, BookingStart, 'booking-start')}
              />
              <Route
                path="/app/:appId/booking/:restaurantId"
                component={load(store, BookingContainer, BookingSelectDate, 'booking-select-date')}
              />
              <Route
                path="/app/:appId/booking/:restaurantId/:bookingDate"
                component={load(store, BookingContainer, BookingTimeSeats, 'booking-time-seats')}
              />

              <Route
                path="/app/:appId/booking/:restaurantId/:bookingDate/:bookingSlot/:seats/checkout"
                component={load(store, BookingContainer, BookingForm, 'booking-form')}
              />
              <Route
                path="/app/:appId/booking/:restaurantId/:bookingDate/:bookingSlot/:seats/end"
                component={load(store, BookingContainer, BookingEnd, 'booking-end')}
              />
              <Route
                path="/app/:appId/user/signin-signup"
                component={SigninSignupPage}
              />
            </Route>
          </Router>
        </Provider>
      </Elements>
    </StripeProvider>,
    document.getElementById('root'),
  );

  document.getElementById('ristoranti-spinner').style.display = 'none';
  if (isInIframe) widgetLoad();
});



// WEBPACK FOOTER //
// ./src/index.js